import React, { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';

import { BufferStockModifierFilter } from './components/buffer-stock-modifier-filter';
import { ForecastPeriodFilter } from './components/forecast-period-filter';
import { LeadTimeModifierFilter } from './components/lead-time-modifier-filter';
import messages from './messages';
import { useForecastingSettings, FORECASTING_SETTING_KEYS } from '../../context/forecasting-settings-provider';
import drawerMessages from '../../messages';
import { LookbackPeriodFilter } from './components/lookback-period-filter';
import { SalesTrendModifierFilter } from './components/sales-trend-modifier-filter';
import style from './filter-container.module.less';

/**
 * FilterContainer component that provides filtering options for the forecasting drawer
 * @returns {React.ReactElement} The rendered FilterContainer component
 */
const FilterContainer = () => {
  const { settings, applySettings, resetSettings } = useForecastingSettings();
  const [localSettings, setLocalSettings] = useState(settings);

  const handleSettingChange = (key, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleLookbackPeriodChange = (value) => {
    handleSettingChange(FORECASTING_SETTING_KEYS.LOOKBACK_PERIOD, value);
  };

  const handleForecastPeriodChange = (value) => {
    handleSettingChange(FORECASTING_SETTING_KEYS.FORECAST_PERIOD, value);
  };

  const handleSalesTrendChange = (value) => {
    handleSettingChange(FORECASTING_SETTING_KEYS.SALES_TREND, value);
  };

  const handleLeadTimeChange = (value) => {
    handleSettingChange(FORECASTING_SETTING_KEYS.LEAD_TIME_DAYS, value);
  };

  const handleBufferStockChange = (value) => {
    handleSettingChange(FORECASTING_SETTING_KEYS.BUFFER_STOCK_DAYS, value);
  };

  const handleApply = () => {
    applySettings(localSettings);
  };

  const handleReset = () => {
    const defaultSettings = resetSettings();
    setLocalSettings(defaultSettings);
  };

  return (
    <FlexRow
      aria-label={<FormattedMessage {...messages.forecastingFiltersAriaLabel} />}
      className={style['filter-container']}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role='region'
    >
      <div className={style['items-wrapper']}>
        {/* Lookback Period */}
        <LookbackPeriodFilter onChange={handleLookbackPeriodChange} value={localSettings.lookbackPeriod} />

        {/* Forecast Period */}
        <ForecastPeriodFilter onChange={handleForecastPeriodChange} value={localSettings.forecastPeriod} />

        {/* Sales Trend Modifier */}
        <SalesTrendModifierFilter onChange={handleSalesTrendChange} value={localSettings.salesTrend} />

        {/* Lead Time Modifier */}
        <LeadTimeModifierFilter onChange={handleLeadTimeChange} value={localSettings.leadTimeDays} />

        {/* Buffer Stock Modifier */}
        <BufferStockModifierFilter onChange={handleBufferStockChange} value={localSettings.bufferStockDays} />
      </div>

      {/* Apply and Reset buttons */}
      <div className={style['buttons-container']}>
        <Button onClick={handleReset} theme='secondary'>
          <FormattedMessage {...drawerMessages.resetSettingsButton} />
        </Button>
        <Button onClick={handleApply} theme='primary'>
          <FormattedMessage {...drawerMessages.applySettingsButton} />
        </Button>
      </div>
    </FlexRow>
  );
};

export default memo(FilterContainer);
